import type {
  SearchParams,
  SearchResponse,
  Article,
  User,
  CreateUserInput,
  UpdateUserInput,
  ArticleInput,
  DateIndex,
} from '@shared/schemas';

export class ApiError extends Error {
  constructor(public status: number, message: string, public detail?: unknown) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      // ignored
    }
    const message =
      body && typeof body === 'object' && 'error' in body
        ? String((body as { error: unknown }).error)
        : `Errore ${res.status}`;
    throw new ApiError(res.status, message, body);
  }
  return (await res.json()) as T;
}

// ─────── Search ───────
export function searchArticles(params: Partial<SearchParams>) {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') sp.set(k, String(v));
  }
  return request<SearchResponse>(`/api/articles?${sp.toString()}`);
}

export function getArticle(id: string) {
  return request<Article>(`/api/articles/${id}`);
}

export function getFonti() {
  return request<{ fonti: string[] }>('/api/articles/sources');
}

export function getDateIndex() {
  return request<{ index: DateIndex }>('/api/articles/index');
}

// ─────── Auth ───────
export type Session = {
  user: { id: string; username: string | null; name: string; role: 'admin' | 'user'; banned: boolean };
  session: { id: string; expiresAt: string };
};

export async function getSession(): Promise<Session | null> {
  try {
    const res = await fetch('/api/auth/get-session', { credentials: 'include' });
    if (!res.ok) return null;
    const data = (await res.json()) as Session | null;
    return data && data.user ? data : null;
  } catch {
    return null;
  }
}

export async function signIn(username: string, password: string): Promise<Session> {
  const res = await fetch('/api/auth/sign-in/username', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { message?: string; error?: string };
    throw new ApiError(res.status, body.message ?? body.error ?? 'Login fallito');
  }
  return (await res.json()) as Session;
}

export async function signOut(): Promise<void> {
  await fetch('/api/auth/sign-out', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: '{}',
  });
}

// ─────── Admin ───────
export function listUsers() {
  return request<{ users: User[] }>('/api/admin/users');
}

export function createUserApi(input: CreateUserInput) {
  return request<{ id: string }>('/api/admin/users', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function updateUserApi(id: string, input: UpdateUserInput) {
  return request<{ id: string }>(`/api/admin/users/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

export function deleteUserApi(id: string) {
  return request<{ ok: true }>(`/api/admin/users/${id}`, {
    method: 'DELETE',
  });
}

export function createArticleApi(input: ArticleInput) {
  return request<{ id: string }>('/api/admin/articles', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function updateArticleApi(id: string, input: ArticleInput) {
  return request<{ id: string }>(`/api/admin/articles/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

export function deleteArticleApi(id: string) {
  return request<{ ok: true }>(`/api/admin/articles/${id}`, {
    method: 'DELETE',
  });
}
